import React from 'react';

import Data, { _F } from 'data';
import { StatTip } from 'components/Paperdoll';

import Store from 'store';
import EditingContext from 'store/editingContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDog, faLock } from '@fortawesome/free-solid-svg-icons';
import Tooltip from 'ui/tooltip';
import NumberInput from 'ui/number';
import BlockClearButton, { builderHasPaperdoll, builderHasStats, builderSelectItemLevel, builderSelectStats, displayStats } from './common';
import Select from 'ui/select';
import { WithItemSetTooltip } from 'tooltip';
import classNames from 'classnames';

function BuilderStat({ id, value, editing, charClass }) {
  if (!value) value = {value: 0};
  const dispatch = Store.useDispatch();
  const limit = Store.useBuilder(builder => {
    let maxSum = null, maxOne = null;
    if (builder.category === 'arena') {
      maxSum = 1000;
      maxOne = 750;
    } else if (builder.category === 'inferno') {
      maxSum = 2200;
      maxOne = 1700;
    } else {
      return 2000;
    }
    if (!builder.stats) return maxOne;
    const sum = Object.values(builder.stats).reduce((sum, value) => sum + (value || 0), 0);
    return Math.min(maxOne, maxSum - sum + (builder.stats[id] || 0));
  }, [id]);
  const onChange = React.useCallback(value => dispatch.setBuilder(builder => {
    builder = {...builder};
    builder.stats = {...builder.stats};
    builder.stats[id] = value;
    return builder;
  }), [dispatch, id]);
  return (
    <Tooltip tooltip={<StatTip id={id} {...value} charClass={charClass}/>}>
      <li>
        <span>{_F(Data.itemStats[id].tip)}</span>
        <span>{editing ? <NumberInput value={value.value} min={0} max={limit} onChange={onChange}/> : value.value}</span>
      </li>
    </Tooltip>
  );
}

const renderSet = ({ id, name }) => (
  <WithItemSetTooltip id={id}>
    <span className="lap-set-bonus">{name}</span>
  </WithItemSetTooltip>
);

function BuilderSetBonus({ editing }) {
  const setBonus = Store.useBuilder(builder => builder.setBonus);
  const charClass = Store.useBuilder(builder => builder.class);
  const dispatch = Store.useDispatch();
  const options = React.useCallback(() => {
    if (!Data.challengeSets) return [];
    return Data.challengeSets.filter(set => Data.itemSet[set.id] && (!set.class || Data.classMap[set.class] === charClass)).map(set => ({
      id: set.id,
      name: Data.itemSet[set.id].name,
    }));
  }, [charClass]);
  const onChange = React.useCallback(opt => dispatch.setBuilder(builder => ({...builder, setBonus: opt?.id})), [dispatch]);
  if (!editing) {
    const set = Data.itemSet[setBonus];
    if (!set) return null;
    return renderSet({id: setBonus, name: set.name});
  }
  return (
    <Select options={options}
            value={Data.itemSet[setBonus] ? ({id: setBonus, name: Data.itemSet[setBonus].name}) : null}
            onChange={onChange}
            renderValue={renderSet}
            renderOption={renderSet}/>
  );
}

export default function BuilderStats() {
  const [showBooks, setShowBooks] = React.useState(true);

  const editing = EditingContext.useStateView("builderEditing");

  const builder = Store.useBuilder();

  const locked = builderHasPaperdoll(builder);

  const stats = React.useMemo(() => builderSelectStats(builder, !editing && showBooks), [builder, editing, showBooks]);

  const gearscore = React.useMemo(() => builderSelectItemLevel(builder), [builder]);

  const onClear = React.useCallback(builder => ({...builder, stats: {}}), []);

  return (
    <div className="lap-BuilderBlock lap-BuilderStats">
      <div className="lap-BuilderBlockHeader">
        Stats
        {!!(locked && editing) && (
          <Tooltip tooltip="When the Items block is used, Stats are determined by equipped accessories.">
            <FontAwesomeIcon className="lap-notice" icon={faLock}/>  
          </Tooltip>
        )}
        {!!(!locked && editing && builderHasStats(builder)) && <BlockClearButton onClear={onClear}/>}
        {!!(locked && !editing) && (
          <Tooltip tooltip={`Show bonus stats from collections and pet`}>
            <FontAwesomeIcon className={classNames("lap-button lap-bonus-stats", {"lap-active": showBooks})} icon={faDog} onClick={() => setShowBooks(s => !s)}/>
          </Tooltip>
        )}
      </div>
      {!!gearscore && (
        <div className="lap-stats lap-gear-stats">
          <h3>{_F('sys.characterinfo.item_level')}</h3>
          <div className="lap-gear-score">{gearscore.toFixed(2)}</div>
        </div>
      )}
      <div className="lap-stats lap-combat-stats">
        <h3>{_F('sys.characterinfo.stat_info_combat')}</h3>
        <ul>
          {displayStats.map(id => (
            <BuilderStat key={id} id={id} value={stats[id] || 0} charClass={builder.class} editing={editing && !locked}/>
          ))}
        </ul>
      </div>
      {!!(builder.category === 'inferno' && (editing || Data.itemSet[builder.setBonus])) && <>
        <div className="lap-BuilderBlockHeader">
          Set Bonuses
        </div>
        <div className="lap-builder-set">
          <BuilderSetBonus editing={editing}/>
        </div>
      </>}
    </div>
  );
}
